/**
 * Login page - Clean minimalist design (Hyperspell style)
 */

import { useState, useEffect, FormEvent } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  signInWithPopup,
  signInAnonymously,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  OAuthProvider,
} from 'firebase/auth';
// getFirebaseAuth imported dynamically when needed
import { useAuthStore } from '@voxapilot/shared';
import { api } from '../config/firebase';
import { ROUTES } from '../config/constants';
import { cn } from '../utils/cn';
import { getErrorMessage, isSessionApiError } from '../utils/authErrors';
import { Navbar } from '../components/landing/Navbar';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated, isLoading: authLoading, user } = useAuthStore();

  // Redirect if already authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated && user) {
      const state = location.state as {from?: {pathname?: string}} | null;
      const from = state?.from?.pathname || ROUTES.APP;
      navigate(from, {replace: true});
    }
  }, [isAuthenticated, user, authLoading, navigate, location]);

  const handleEmailSignIn = async (e: FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // FIXED: Ensure Firebase is initialized before using auth
      // Import async version to wait for initialization if needed
      const { getFirebaseAuthAsync } = await import('@voxapilot/shared');
      const auth = await getFirebaseAuthAsync();
      const result = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = result.user;

      // Try to create session, but don't fail if it doesn't work
      // Session API is optional - Firebase Auth is sufficient
      let sessionWarning: string | null = null;

      try {
        const token = await firebaseUser.getIdToken();
        const sessionResponse = await api.session.createSession(token);

        if (!sessionResponse.success) {
          // Session API failed, but continue with Firebase Auth
          sessionWarning = 'Session creation failed, but you can still continue.';
          console.warn('Session API failed, continuing with Firebase Auth:', sessionResponse.error);
        } else {
          // Session created successfully, try to initialize with session API
          const { initialize } = useAuthStore.getState();
          try {
            await initialize(api.session);
            setIsLoading(false);
            
            // Navigate after successful sign-in
            const state = location.state as {from?: {pathname?: string}} | null;
            const from = state?.from?.pathname || ROUTES.APP;
            navigate(from, {replace: true});
            return; // Success with session API
          } catch (initError) {
            console.warn('Session initialization failed, using Firebase Auth directly:', initError);
            sessionWarning = 'Session initialization failed, but you can still continue.';
          }
        }
      } catch (sessionError: any) {
        // Session API error - log but don't fail
        if (isSessionApiError(sessionError)) {
          sessionWarning = 'Session creation failed, but you can still continue.';
          console.warn('Session API error, continuing with Firebase Auth:', sessionError);
        } else {
          // Non-session error - might be a real problem, but still try to continue
          console.warn('Unexpected error during session creation, continuing with Firebase Auth:', sessionError);
          sessionWarning = 'Session creation had issues, but you can still continue.';
        }
      }

      // Continue with Firebase Auth (session API is optional)
      const { initialize } = useAuthStore.getState();
      try {
        await initialize(); // Use Firebase Auth directly
        setIsLoading(false);

        // Show warning if session API failed, but don't treat it as an error
        if (sessionWarning) {
          console.info(sessionWarning);
          // Optionally show a non-blocking info message to user
          // For now, we'll just log it - user is still authenticated
        }

        // Navigate after successful sign-in
        const state = location.state as {from?: {pathname?: string}} | null;
        const from = state?.from?.pathname || ROUTES.APP;
        navigate(from, {replace: true});
      } catch (initError) {
        // Even Firebase Auth initialization failed - this is a real error
        throw initError;
      }
    } catch (err: any) {
      // Log detailed error information for debugging
      console.error('[Login] Sign-in error:', {
        code: err?.code,
        message: err?.message,
        stack: err?.stack,
        fullError: err,
      });

      // If it's a Firebase Auth error, log additional details
      if (err?.code?.startsWith('auth/')) {
        console.error('[Login] Firebase Auth error code:', err.code);
        console.error('[Login] Full error object:', JSON.stringify(err, null, 2));
      }

      // Log Firebase config status
      try {
        const { getApps } = await import('firebase/app');
        const apps = getApps();
        if (apps.length > 0) {
          const app = apps[0];
          console.error('[Login] Firebase config:', {
            apiKey: app.options?.apiKey ? `${app.options.apiKey.substring(0, 10)}...` : 'MISSING',
            authDomain: app.options?.authDomain,
            projectId: app.options?.projectId,
          });
        } else {
          console.error('[Login] No Firebase app initialized!');
        }
      } catch (configError) {
        console.error('[Login] Failed to check Firebase config:', configError);
      }

      // Use error message utility for user-friendly messages
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
      setIsLoading(false);
    }
  };

  const handleOAuthSignIn = async (provider: 'google' | 'apple') => {
    try {
      setIsLoading(true);
      setError(null);

      // FIXED: Ensure Firebase is initialized before using auth
      const { getFirebaseAuthAsync } = await import('@voxapilot/shared');
      const auth = await getFirebaseAuthAsync();
      const isEmulator = import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true';

      let firebaseUser;

      if (isEmulator) {
        const result = await signInAnonymously(auth);
        firebaseUser = result.user;
      } else {
        let providerInstance;
        if (provider === 'google') {
          providerInstance = new GoogleAuthProvider();
        } else if (provider === 'apple') {
          providerInstance = new OAuthProvider('apple.com');
        } else {
          throw new Error('Unsupported provider');
        }

        const result = await signInWithPopup(auth, providerInstance);
        firebaseUser = result.user;
      }

      // Check if this is a new user using Firebase metadata
      // Compare creationTime with lastSignInTime - if they're the same, it's a new user
      const metadata = firebaseUser.metadata;
      const isNewUser = metadata.creationTime === metadata.lastSignInTime;

      if (isNewUser) {
        // This is a new user - sign them out and redirect to registration
        await auth.signOut();
        setIsLoading(false);
        setError(
          'No account found. Please create an account first using email and password. ' +
          'OAuth sign-in is only available for existing accounts.'
        );
        setTimeout(() => {
          navigate(ROUTES.SIGN_UP, {
            state: {
              message: 'Please create an account with email and password first. OAuth sign-in will be available after you create your account.',
              email: firebaseUser.email || undefined,
            },
          });
        }, 3000);
        return;
      }

      // Existing user - try to create session, but don't fail if it doesn't work
      // Session API is optional - Firebase Auth is sufficient
      let sessionWarning: string | null = null;

      try {
        const token = await firebaseUser.getIdToken();

        // Create session
        const sessionResponse = await api.session.createSession(token);

        if (!sessionResponse.success) {
          // Session API failed, but continue with Firebase Auth
          sessionWarning = 'Session creation failed, but you can still continue.';
          console.warn('Session API failed, continuing with Firebase Auth:', sessionResponse.error);
        } else {
          // Session created successfully, try to initialize with session API
          const { initialize } = useAuthStore.getState();
          try {
            await initialize(api.session);
            setIsLoading(false);
            
            // Navigate after successful OAuth sign-in
            const state = location.state as {from?: {pathname?: string}} | null;
            const from = state?.from?.pathname || ROUTES.APP;
            navigate(from, {replace: true});
            return; // Success with session API
          } catch (initError) {
            console.warn('Session initialization failed, using Firebase Auth directly:', initError);
            sessionWarning = 'Session initialization failed, but you can still continue.';
          }
        }
      } catch (sessionError: any) {
        // Session API error - log but don't fail
        if (isSessionApiError(sessionError)) {
          sessionWarning = 'Session creation failed, but you can still continue.';
          console.warn('Session API error, continuing with Firebase Auth:', sessionError);
        } else {
          // Non-session error - might be a real problem, but still try to continue
          console.warn('Unexpected error during session creation, continuing with Firebase Auth:', sessionError);
          sessionWarning = 'Session creation had issues, but you can still continue.';
        }
      }

      // Continue with Firebase Auth (session API is optional)
      const { initialize } = useAuthStore.getState();
      try {
        await initialize(); // Use Firebase Auth directly
        setIsLoading(false);

        // Show warning if session API failed, but don't treat it as an error
        if (sessionWarning) {
          console.info(sessionWarning);
          // Optionally show a non-blocking info message to user
          // For now, we'll just log it - user is still authenticated
        }

        // Navigate after successful OAuth sign-in
        const state = location.state as {from?: {pathname?: string}} | null;
        const from = state?.from?.pathname || ROUTES.APP;
        navigate(from, {replace: true});
      } catch (initError) {
        // Even Firebase Auth initialization failed - this is a real error
        throw initError;
      }
    } catch (err: any) {
      // Log detailed error information for debugging
      console.error('[Login] OAuth sign-in error:', {
        code: err?.code,
        message: err?.message,
        stack: err?.stack,
        fullError: err,
      });

      // If it's a Firebase Auth error, log additional details
      if (err?.code?.startsWith('auth/')) {
        console.error('[Login] Firebase Auth error code:', err.code);
        console.error('[Login] Full error object:', JSON.stringify(err, null, 2));
      }

      // Log Firebase config status
      try {
        const { getApps } = await import('firebase/app');
        const apps = getApps();
        if (apps.length > 0) {
          const app = apps[0];
          console.error('[Login] Firebase config:', {
            apiKey: app.options?.apiKey ? `${app.options.apiKey.substring(0, 10)}...` : 'MISSING',
            authDomain: app.options?.authDomain,
            projectId: app.options?.projectId,
          });
        } else {
          console.error('[Login] No Firebase app initialized!');
        }
      } catch (configError) {
        console.error('[Login] Failed to check Firebase config:', configError);
      }

      // Use error message utility for user-friendly messages
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background dark:bg-[#1E1E1E] flex flex-col">
      {/* Full Navbar */}
      <Navbar />

      {/* Main Content - Centered Card */}
      <div className="flex-1 flex items-center justify-center px-4 pb-12 pt-20">
        <div className="w-full max-w-md">
          {/* Logo - Right above the card */}
          <div className="flex justify-center mb-4">
            <Link to={ROUTES.HOME} className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gray-900 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">V</span>
              </div>
              <span className="font-heading font-bold text-xl text-textPrimary dark:text-[#CCCCCC]">VoxaPilot</span>
            </Link>
          </div>

          {/* Card */}
          <div className="bg-white dark:bg-[#252526] rounded-2xl p-8 shadow-sm border border-gray-200 dark:border-gray-700">
            {/* Title */}
            <h1 className="text-2xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-2">
              Sign in
            </h1>

            {/* Description */}
            <p className="text-sm text-textSecondary dark:text-[#858585] mb-6">
              Sign in to your VoxaPilot account to continue
            </p>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            {/* Email/Password Form */}
            <form onSubmit={handleEmailSignIn} className="space-y-4 mb-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-textPrimary dark:text-[#CCCCCC] mb-2">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  placeholder="Enter your email address"
                  className={cn(
                    'w-full px-4 py-3 rounded-lg border',
                    'bg-white dark:bg-[#1E1E1E] text-textPrimary dark:text-[#CCCCCC]',
                    'placeholder:text-gray-400 dark:placeholder:text-[#6A6A6A]',
                    'focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-700 focus:border-transparent',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                    'transition-colors',
                    error ? 'border-red-300 dark:border-red-700' : 'border-gray-200 dark:border-gray-700'
                  )}
                  autoComplete="email"
                  required
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label htmlFor="password" className="block text-sm font-medium text-textPrimary dark:text-[#CCCCCC]">
                    Password
                  </label>
                  <Link
                    to={ROUTES.FORGOT_PASSWORD}
                    className="text-sm text-textSecondary dark:text-[#858585] hover:text-textPrimary dark:hover:text-[#CCCCCC] underline"
                  >
                    Forgot password?
                  </Link>
                </div>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  placeholder="Enter your password"
                  className={cn(
                    'w-full px-4 py-3 rounded-lg border',
                    'bg-white dark:bg-[#1E1E1E] text-textPrimary dark:text-[#CCCCCC]',
                    'placeholder:text-gray-400 dark:placeholder:text-[#6A6A6A]',
                    'focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-700 focus:border-transparent',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                    'transition-colors',
                    error ? 'border-red-300 dark:border-red-700' : 'border-gray-200 dark:border-gray-700'
                  )}
                  autoComplete="current-password"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className={cn(
                  'w-full py-3 px-4 rounded-lg font-medium',
                  'bg-gray-900 dark:bg-orange-600 text-white',
                  'hover:bg-gray-800 dark:hover:bg-orange-700',
                  'focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-orange-500 focus:ring-offset-2',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'transition-colors'
                )}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Signing in...
                  </span>
                ) : (
                  'Sign in'
                )}
              </button>
            </form>

            {/* Don't have an account */}
            <div className="mt-4 text-center">
              <p className="text-sm text-textSecondary dark:text-[#858585]">
                Don't have an account?{' '}
                <Link
                  to={ROUTES.SIGN_UP}
                  className="text-textPrimary dark:text-[#CCCCCC] font-medium underline hover:text-gray-700 dark:hover:text-gray-300"
                >
                  Create new account
                </Link>
              </p>
            </div>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white dark:bg-[#252526] text-gray-500 dark:text-[#858585]">Or continue with</span>
              </div>
            </div>

            {/* OAuth Buttons */}
            <div className="space-y-3">
              {/* Google Sign-In */}
              <button
                onClick={() => handleOAuthSignIn('google')}
                disabled={isLoading}
                className={cn(
                  'w-full flex items-center justify-center gap-3 px-4 py-3',
                  'border border-gray-200 dark:border-gray-700 rounded-lg',
                  'bg-white dark:bg-[#2D2D30] text-gray-900 dark:text-[#CCCCCC]',
                  'hover:bg-gray-50 dark:hover:bg-[#3C3C3C]',
                  'focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-400 focus:ring-offset-2',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'transition-colors'
                )}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                <span>Continue with Google</span>
              </button>

              {/* Apple Sign-In */}
              <button
                onClick={() => handleOAuthSignIn('apple')}
                disabled={isLoading}
                className={cn(
                  'w-full flex items-center justify-center gap-3 px-4 py-3',
                  'border border-gray-200 dark:border-gray-700 rounded-lg',
                  'bg-white dark:bg-[#2D2D30] text-gray-900 dark:text-[#CCCCCC]',
                  'hover:bg-gray-50 dark:hover:bg-[#3C3C3C]',
                  'focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-400 focus:ring-offset-2',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'transition-colors'
                )}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                </svg>
                <span>Continue with Apple</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
